
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ItineraryEvent, Trip } from '@/types/itinerary';
import { useEventForm } from '@/hooks/useEventForm';
import EventFormFields from './EventFormFields';
import EventFormActions from './EventFormActions';

interface EventFormProps {
  trip: Trip | null;
  event: ItineraryEvent | null; // Null for new event, event object for edit
  onClose: () => void;
  selectedDate: string;
}

const EventForm: React.FC<EventFormProps> = ({ 
  trip, 
  event, 
  onClose,
  selectedDate 
}) => {
  const {
    formData,
    isSubmitting,
    open,
    isEditMode,
    handleChange,
    handleSwitchChange,
    handleSubmit,
    handleClose,
    setOpen
  } = useEventForm({ trip, event, onClose, selectedDate });
  
  // Make sure dialog closes properly
  useEffect(() => {
    if (!open) {
      // Small delay to allow the dialog closing animation
      const timer = setTimeout(() => {
        onClose();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, onClose]);
  
  return (
    <Dialog open={open} onOpenChange={(value) => {
      setOpen(value);
      if (!value) handleClose();
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Event' : 'Add New Event'}</DialogTitle>
          <DialogDescription>
            Fill in the details for your itinerary event.
          </DialogDescription>
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-4 top-4" 
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <EventFormFields 
            formData={formData}
            handleChange={handleChange}
            handleSwitchChange={handleSwitchChange}
          />
          
          <EventFormActions
            isSubmitting={isSubmitting}
            isEditMode={isEditMode}
            onCancel={handleClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventForm;
